import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule, HAMMER_LOADER } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService
} from '@azure/msal-angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AUDITLIB_CONFIG, Amap3Module, GeneralFilterPipe, ServicesModule, TableFilterPipe, DatePipe, DeepObjectFilterPipe, SortPipe, ThousandPipe } from '@mapfre-tech/amap3';
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";
import { ToastModule } from 'primeng/toast';
import { environment } from '../environments/environment';
import { CoreModule } from './@core/core.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import localeTr from '@angular/common/locales/tr';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import 'froala-editor/js/languages/es.js';
import 'froala-editor/js/languages/pt_br.js';
import 'froala-editor/js/languages/pt_pt.js';
import 'froala-editor/js/languages/tr.js';
import { InjectorService } from './@core/data/injector/injector.service';
import { GuardsModule } from './@core/guards/guards.module';
import { AuthInterceptor } from './@core/interceptors/auth.interceptor';
import { InterceptorsModule } from './@core/interceptors/interceptors.module';
import { LoadingInterceptor } from './@core/interceptors/loading.interceptor';

registerLocaleData(localeEs, 'es')
registerLocaleData(localeEn, 'en')
registerLocaleData(localePt, 'pt')
registerLocaleData(localeTr, 'tr')

// MsalInterceptor will obtain tokens and add them to all your Http requests in API calls based on the protectedResourceMap
//lista de direcciones protegidas (con token) de mi API si uso interceptors
export const protectedResourceMap: [string, string[]][] = [
  ['https://graph.microsoft.com/v1.0/me', ['user.read']],
  ["./assets/i18n/*.json", ['null']],
  [environment.base_URL, [environment.scope]],
  [environment.base_URL_GAIA, [environment.scope]],
];
const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;


export function loggerCallback(logLevel: LogLevel, message: string) {
  // acquireTokenSilent rejection on startup is expected behaviour (MSAL triggers interactive flow to resolve)
  if (message.includes('acquireTokenSilent rejected')) return;
  switch (logLevel) {
    case LogLevel.Error:
      console.error(message);
      break;
    case LogLevel.Warning:
      console.warn(message);
      break;
    default:
      console.log(message);
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId, // application ID
      authority: environment.authority, // tenant ID
      // validateAuthority: true,
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: environment.redirectUri,
      //navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(environment.base_URL, [environment.scope]);
  protectedResourceMap.set(environment.base_URL_GAIA, [environment.scope]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    authRequest: {
      extraScopesToConsent: [
            'user.read',
            'openid',
            'profile',
            environment.scope
          ]
    }

    
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { interactionType: InteractionType.Redirect };
}


// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: `${environment.base_URL}/translations/`, suffix: "" },
    // version as query param so browsers don't reuse cached translations from a previous release
    // (CloudFront ignores query strings in its cache key, but each deploy invalidates /*)
    { prefix: "./assets/i18n/", suffix: `.json?v=${environment.version}` },
  ]);
}
@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      useDefaultLang: false
    }),
    GuardsModule.forRoot(),
    ServicesModule.forRoot(environment),
    ToastModule,
    Amap3Module,
    InterceptorsModule,
    CoreModule,
    MsalModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    GeneralFilterPipe,
    TableFilterPipe,
    DatePipe,
    DeepObjectFilterPipe,
    SortPipe,
    ThousandPipe,
    {
      provide: AUDITLIB_CONFIG,
      useValue: {
        apiBaseUrl: environment.base_URL,
        gaiaBaseUrl: environment.base_URL_GAIA,
        translationsBaseUrl: environment.base_URL + '/translations'
      }
    },
    { provide: LOCALE_ID, useValue: 'es' },
    { provide: LOCALE_ID, useValue: 'en' },
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: HAMMER_LOADER, useValue: () => {
      if (!(window as any)['Hammer']) {
        (window as any)['Hammer'] = class {
          get() { return { set: () => this }; }
          on() {}
        };
      }
      return Promise.resolve();
    }},
    provideHttpClient(withInterceptors([AuthInterceptor, LoadingInterceptor]), withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
  constructor(private injector: Injector) {
    // Create global Service Injector.
    InjectorService.injector = this.injector;
  }
}


